import type { SearchHit, SearchResponse } from '@/types/sammy/response';
import type { Filter, FilterOption } from '@/types/filters';
import { convertAggregationsToFilters, convertFiltersToQuery, restoreSelectedFilters } from '~/utils/filters';
import { getObjectsFromQuery } from '~/utils/getObjectsFromQuery';
import log from '~/utils/log';

interface ProductsResponse {
    products: SearchHit[];
    totalFilteredProductCount: number;
    filters: Filter[];
    selectedFilters: Record<string, FilterOption>;
}

interface UseProductSearchOptions {
    limit?: number;
}

export function useFachportalProductSearch(options: UseProductSearchOptions = {}) {
    const limit = options.limit ?? 12;
    const route = useRoute();
    const { t } = useI18n();
    const { country, lang, urlSegment } = useLanguage();
    const requestFetch = useRequestFetch();

    const fetchedProducts = ref<SearchHit[]>([]);
    const filters = ref<Filter[]>([]);
    const selectedFilters = ref<Record<string, FilterOption>>({});
    const pendingMore = ref(false);
    const inFlightOffset = ref<number | null>(null);
    const order = computed(() => route.query.order?.toString() || 'asc');
    const selectedFiltersCount = computed(() => Object.values(selectedFilters.value).length);
    const selectedFiltersParams = computed(() => convertFiltersToQuery(selectedFilters));

    const totalCountKey = computed(() => `total-product-count-${urlSegment.value}`);
    const queryKey = computed(() => `fachkreis-plp-products-${urlSegment.value}-${JSON.stringify(route.query ?? {})}`);

    const buildSearchQuery = (query: Record<string, unknown> = {}) => ({
        country: country.value,
        lang: lang.value,
        ...query
    });

    const fetchProducts = (query: Record<string, unknown> = {}) => {
        const params = { query: buildSearchQuery(query) };

        return requestFetch<SearchResponse>('/api/search/products', params);
    };

    const getFiltersFromResponse = (response: SearchResponse) => {
        const newFilters = convertAggregationsToFilters(response.es_result?.aggregations ?? {}, t);
        const objects = getObjectsFromQuery(route.query as Record<string, string>);
        const { filters: restoredFilters, selectedFilters: restoredSelectedFilters } = restoreSelectedFilters(
            newFilters,
            objects.filters
        );

        return {
            filters: restoredFilters,
            selectedFilters: restoredSelectedFilters
        };
    };

    const getTotalFilteredProductCount = (response: SearchResponse) => response.es_result?.hits?.total?.value || 0;
    const getProductsFromResponse = (response: SearchResponse): SearchHit[] => response.es_result?.hits?.hits || [];

    const transformResponse = (response: SearchResponse): ProductsResponse => {
        const { filters, selectedFilters } = getFiltersFromResponse(response);

        return {
            products: getProductsFromResponse(response),
            totalFilteredProductCount: getTotalFilteredProductCount(response),
            filters,
            selectedFilters
        };
    };

    function syncStateFromInitialData(data?: ProductsResponse | null) {
        fetchedProducts.value = [];

        if (!data) return;

        filters.value = data.filters;
        selectedFilters.value = data.selectedFilters;
    }

    function mergeMoreProducts(response: SearchResponse, initialProductsCount = 0) {
        const nextProducts = getProductsFromResponse(response);
        const nextOffset = initialProductsCount + fetchedProducts.value.length;

        fetchedProducts.value = [...fetchedProducts.value, ...nextProducts];
        return nextOffset;
    }

    function omitSelectedFilter(type: string) {
        const { [type]: _removedFilter, ...rest } = selectedFilters.value;
        selectedFilters.value = rest;
    }

    async function fetchMoreProducts(currentProductsLength: number) {
        if (pendingMore.value || inFlightOffset.value === currentProductsLength) {
            return;
        }

        pendingMore.value = true;
        inFlightOffset.value = currentProductsLength;

        if (currentProductsLength === 0 || currentProductsLength % limit !== 0) {
            pendingMore.value = false;
            inFlightOffset.value = null;
            return;
        }

        try {
            const response = await fetchProducts({
                from: currentProductsLength,
                size: limit,
                order: order.value,
                ...selectedFiltersParams.value
            });

            mergeMoreProducts(response, currentProductsLength - fetchedProducts.value.length);
        } catch (error) {
            log.error('error while fetching more search results', error);
            fetchedProducts.value = [];
        } finally {
            pendingMore.value = false;
            inFlightOffset.value = null;
        }
    }

    function updateFilter(type: string, newValue: FilterOption) {
        if (newValue.key === 'all') {
            omitSelectedFilter(type);
            return;
        }

        selectedFilters.value[type] = newValue;
    }

    function resetFilters() {
        selectedFilters.value = {};
        filters.value = [];
    }

    function removeSelectedFilter(type: string) {
        omitSelectedFilter(type);
        filters.value = [];
    }

    return {
        fetchedProducts,
        fetchMoreProducts,
        fetchProducts,
        filters,
        order,
        pendingMore,
        queryKey,
        removeSelectedFilter,
        resetFilters,
        selectedFilters,
        selectedFiltersCount,
        selectedFiltersParams,
        syncStateFromInitialData,
        totalCountKey,
        transformResponse,
        updateFilter
    };
}
